import { useMemo, useState } from "react"
import { SelectChangeEvent, Tooltip } from "@mui/material"
import { DateTime } from "luxon"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"
import { MRT_ColumnDef } from "material-react-table"
import { faUserGroup, faLocationDot, faArrowsRotate } from "@fortawesome/pro-light-svg-icons"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { TableNumberOfItemsBox, TableSummaryWrap } from "../../../../../../shared/components/table/TableSummaryBox"
import {
    fetchUserResourceBookingArchive,
    FetchUserResourceBookingArchiveResponse,
    ArchiveResourceBooking,
} from "../userResourceBookingArchiveApi"
import { useAppSelector } from "../../../../../store/storeHooks"
import { ResourceBooking } from "../../../upcomingOrders/resourceBookings/userResourceBookingTypes"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { selectUserId } from "../../../../../login/loginSelectors"
import { ResourceType } from "../../../../../serviceCenter/admin/organization/resources/resourcesTypes"
import { MeetingCardMenu } from "../../../home/components/upcomingOrders/meetings/MeetingCardMenu"
import { MenuMeeting } from "../../../home/weekMenuTypes"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"

const useStyles = makeStyles()(() => ({
    wrapIconText: {
        marginRight: ".5rem",
        color: tailwindPalette.slate[500],
    },
}))

const selectOrders = (data: FetchUserResourceBookingArchiveResponse) => data?.orders || []

export function UserResourceBookingArchive() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const userId = useAppSelector(selectUserId)
    const [selectedResources, setSelectedResources] = useState<ResourceType[]>([])

    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })

    const resourceBookingArchiveResult = useApiQuery(fetchUserResourceBookingArchive, {
        queryName: ArchiveResourceBooking.ARCHIVE_RESOURCE_BOOKING,
        dependencies: {
            userId: userId ?? 0,
            resourceTypes: selectedResources.length ? selectedResources.join(",") : undefined,
            startDate: dateState.startDate?.toISODate(),
            endDate: dateState.endDate?.toISODate(),
        },
        select: selectOrders,
    })

    // keep this function resource type filter
    const handleResourceTypeChange = (event: SelectChangeEvent<typeof selectedResources>) => {
        const {
            target: { value },
        } = event
        const selected = typeof value === "string" ? value.split(",") : value

        if (selected.length === selectedResources.length) {
            setSelectedResources([])
        } else {
            setSelectedResources(selected as ResourceType[])
        }
    }

    const mapResourceBookingToMeeting = (booking: ResourceBooking): MenuMeeting => {
        const { meetingName, startDateTime, endDateTime } = booking.meetingDetails

        return {
            id: booking.id,
            uid: booking.uid,
            orderSummary: booking.orderSummary,
            orderAction: booking.orderAction,
            organizers: booking.organizers,
            meetingDetails: {
                meetingName,
                startDateTime,
                endDateTime,
            },
        }
    }

    const { ApiQueryTable, columnFactory } = useApiQueryTable(resourceBookingArchiveResult)

    const columns = useMemo<MRT_ColumnDef<ResourceBooking>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("user-upcoming-orders-resource-booking-table-id"),
                accessorKey: "id",
            }),
            columnFactory.datetimeCompactPeriod(
                {
                    header: l10n.getString("user-upcoming-orders-resource-booking-table-time"),
                    accessorKey: "meetingDetails.startDateTime",
                },
                (row) => row.meetingDetails.startDateTime,
                (row) => row.meetingDetails.endDateTime,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("user-upcoming-orders-resource-booking-table-meeting"),
                accessorKey: "meetingDetails",
                Cell: ({ row }) => {
                    return (
                        <StandardTwoLinesCell
                            title={row.original.meetingDetails.meetingName}
                            subtitle1={
                                <>
                                    <FontAwesomeIcon icon={faLocationDot} className={classes.wrapIconText} />
                                    {row.original.meetingDetails.location}
                                </>
                            }
                            subtitle2={
                                <>
                                    <FontAwesomeIcon icon={faUserGroup} className={classes.wrapIconText} />
                                    {row.original.orderSummary.participants}
                                </>
                            }
                            subtitle3={
                                row.original.meetingDetails?.recurrence ? (
                                    <FontAwesomeIcon icon={faArrowsRotate} className={classes.wrapIconText} />
                                ) : undefined
                            }
                        />
                    )
                },
            }),
            columnFactory.statusColumn({
                header: l10n.getString("user-upcoming-orders-resource-booking-table-visibility"),
                id: "visibility",
                accessorFn: (row) => (row.meetingDetails.isPrivate ? "yes" : "no"),
                Cell: ({ row }) => {
                    return (
                        <Tooltip
                            arrow
                            title={
                                row.original.meetingDetails.isPrivate
                                    ? l10n.getString(
                                          "user-upcoming-orders-resource-booking-table-visibility-private-tooltip",
                                      )
                                    : l10n.getString(
                                          "user-upcoming-orders-resource-booking-table-visibility-public-tooltip",
                                      )
                            }
                        >
                            <div>
                                {!row.original.meetingDetails.isPrivate ? (
                                    <StandardChip
                                        label={l10n.getString(
                                            "user-upcoming-orders-resource-booking-table-visibility-public",
                                        )}
                                        variant="success"
                                        placement="table"
                                    />
                                ) : (
                                    <StandardChip
                                        label={l10n.getString(
                                            "user-upcoming-orders-resource-booking-table-visibility-private",
                                        )}
                                        variant="red"
                                        placement="table"
                                    />
                                )}
                            </div>
                        </Tooltip>
                    )
                },
            }),
        ],
        [l10n, columnFactory],
    )

    return (
        <>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <MeetingCardMenu
                        meetings={resourceBookingArchiveResult.data}
                        ignoreMenuContainer
                        meeting={mapResourceBookingToMeeting(row.original)}
                        menuOnApi={false}
                        handleMenuClose={closeMenu}
                    />,
                ]}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        {/* <ResourceTypeFilter value={selectedResources} onChange={handleResourceTypeChange} /> */}
                        <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                renderBottomToolbarCustomActions={({ table }) => (
                    <TableSummaryWrap>
                        <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                            <TableNumberOfItemsBox
                                data={table.getFilteredRowModel().rows}
                                isLoading={resourceBookingArchiveResult.isLoading}
                                title="Number of orders"
                            />
                        </LocalizedStrict>
                    </TableSummaryWrap>
                )}
            />
        </>
    )
}

